import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AnimatePresence } from "framer-motion";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";

// Pages
import HomePage from "@/pages/HomePage";
import ConciergePage from "@/pages/ConciergePage";
import MagazinePage from "@/pages/MagazinePage";
import MembershipPage from "@/pages/MembershipPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AdminApplicationsPage from "@/pages/AdminApplicationsPage";
import AdminInboxPage from "@/pages/AdminInboxPage";
import AdminScanPage from "@/pages/AdminScanPage";
import MatchingPage from "@/pages/MatchingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AboutPage from "@/pages/AboutPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import PublicMemberPage from "@/pages/PublicMemberPage";
import AccountProfilePage from "@/pages/AccountProfilePage";
import AccountOverviewPage from "@/pages/account/AccountOverviewPage";
import AccountCardPage from "@/pages/account/AccountCardPage";
import AccountRequestsPage from "@/pages/account/AccountRequestsPage";
import AccountSettingsPage from "@/pages/account/AccountSettingsPage";
import AccountServicesPage from "@/pages/account/AccountServicesPage";
import AccountMembershipPage from "@/pages/account/AccountMembershipPage";
import AccountDirectoryPage from "@/pages/account/AccountDirectoryPage";
import LanguageSync from "@/components/LanguageSync";
import SocialBubble from "@/components/vangard/SocialBubble";
import AdminMembersPage from "@/pages/AdminMembersPage";
import MafiaHubPage from "@/pages/mafia/MafiaHubPage";
import MafiaRolesPage from "@/pages/mafia/MafiaRolesPage";
import MafiaNewGamePage from "@/pages/mafia/MafiaNewGamePage";
import MafiaGamePage from "@/pages/mafia/MafiaGamePage";
import MafiaHistoryPage from "@/pages/mafia/MafiaHistoryPage";
import MafiaLoginPage from "@/pages/mafia/MafiaLoginPage";
import GamerAuthCallback from "@/pages/mafia/GamerAuthCallback";
import DashboardOverviewPage from "@/pages/mafia/DashboardOverviewPage";
import DashboardCardPage from "@/pages/mafia/DashboardCardPage";
import DashboardHistoryPage from "@/pages/mafia/DashboardHistoryPage";
import DashboardSettingsPage from "@/pages/mafia/DashboardSettingsPage";
import DashboardNetworkPage from "@/pages/mafia/DashboardNetworkPage";
import HostDashboardPage from "@/pages/mafia/HostDashboardPage";
import HostGamesPage, { HostSettingsPage } from "@/pages/mafia/HostGamesPage";
import HostScoringPage from "@/pages/mafia/HostScoringPage";
import HostPlayersPage from "@/pages/mafia/HostPlayersPage";
import PublicProfilePage from "@/pages/mafia/PublicProfilePage";

// Components

// Context
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { MafiaAuthProvider } from "@/context/MafiaAuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function AppRouter() {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* --------------------- VangardClub (root) --------------------- */}
        <Route path="/" element={<HomePage />} />
        <Route path="/vangard" element={<Navigate to="/" replace />} />
        <Route path="/explore" element={<Navigate to="/" replace />} />
        <Route path="/concierge" element={<ConciergePage />} />
        <Route path="/concierge-service" element={<Navigate to="/account/services" replace />} />
        <Route path="/services" element={<Navigate to="/account/services" replace />} />
        <Route path="/card" element={<Navigate to="/account/card" replace />} />
        <Route path="/membership" element={<MembershipPage />} />
        <Route path="/magazine" element={<MagazinePage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/matching" element={<MatchingPage />} />
        <Route path="/admin/applications" element={<AdminApplicationsPage />} />
        <Route path="/admin/inbox" element={<AdminInboxPage />} />
        <Route path="/admin/scan" element={<AdminScanPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/vangard/login" element={<Navigate to="/login" replace />} />
        <Route path="/vangard/register" element={<Navigate to="/register" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/u/:slug" element={<PublicMemberPage />} />
        <Route path="/account" element={<AccountOverviewPage />} />
        <Route path="/account/profile" element={<AccountProfilePage />} />
        <Route path="/account/card" element={<AccountCardPage />} />
        <Route path="/account/concierge" element={<AccountRequestsPage />} />
        <Route path="/account/directory" element={<AccountDirectoryPage />} />
        <Route path="/account/services" element={<AccountServicesPage />} />
        <Route path="/account/membership" element={<AccountMembershipPage />} />
        <Route path="/account/settings" element={<AccountSettingsPage />} />
        <Route path="/admin/members" element={<AdminMembersPage />} />

        {/* --------------------- Mafia (under /mafia) --------------------- */}
        <Route path="/mafia" element={<MafiaHubPage />} />
        <Route path="/mafia/roles" element={<MafiaRolesPage />} />
        <Route path="/mafia/new" element={<MafiaNewGamePage />} />
        <Route path="/mafia/history" element={<MafiaHistoryPage />} />
        <Route path="/mafia/game/:id" element={<MafiaGamePage />} />
        <Route path="/mafia/login" element={<MafiaLoginPage />} />
        <Route path="/gamer/auth/callback" element={<GamerAuthCallback />} />
        <Route path="/profile" element={<Navigate to="/dashboard/card" replace />} />
        <Route path="/dashboard" element={<DashboardOverviewPage />} />
        <Route path="/dashboard/card" element={<DashboardCardPage />} />
        <Route path="/dashboard/history" element={<DashboardHistoryPage />} />
        <Route path="/dashboard/network" element={<DashboardNetworkPage />} />
        <Route path="/dashboard/settings" element={<DashboardSettingsPage />} />
        <Route path="/p/:memberNo" element={<PublicProfilePage />} />
        <Route path="/host" element={<HostDashboardPage />} />
        <Route path="/host/games" element={<HostGamesPage />} />
        <Route path="/host/scoring" element={<HostScoringPage />} />
        <Route path="/host/players" element={<HostPlayersPage />} />
        <Route path="/host/roles" element={<MafiaRolesPage />} />
        <Route path="/host/settings" element={<HostSettingsPage />} />
      </Routes>
    </AnimatePresence>
  );
}

function AppContent() {
  return (
    <div className="min-h-screen bg-[#050505] noise-overlay">
      <LanguageSync />
      <AppRouter />
      <SocialBubble />
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <MafiaAuthProvider>
                <AppContent />
              </MafiaAuthProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
